import type { UiEventData } from '@rango-dev/widget-embedded';

import { useStackModal } from '@rango-dev/internal-profile/hooks';
import { Box, Button, Flex, IconButton, Spinner, Text, Tooltip } from '@rango-dev/whale';
import {
  UiEventTypes,
  useWallets,
  useWidget,
  widgetEventEmitter,
  WidgetEvents,
} from '@rango-dev/widget-embedded';
import clsx from 'clsx';
import { Plus, Wallet } from 'lucide-react';
import React, { type FunctionComponent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { GTMEvents } from '../../constants/events';
import { CONNECT_WALLETS_MODAL_ID, WALLET_DETAILS_MODAL_ID } from '../../constants/modals';
import { WIDGET_BASE_ROUTE } from '../../constants/navigation';
import { useAppSelector } from '../../hooks/reduxHooks';
import { getContainer } from '../../utils/generalFunctions/common';
import { numberToString } from '../../utils/Numbers';
import ConnectWalletModal from '../ConnectWalletModal';
import { WalletDetails } from '../WalletDetails';

import styles from './ConnectWalletButton.module.css';

const WALLET_DISPLAY_LIMIT = 3;
const WALLET_ICON_SIZE = 18;
const ADD_ICON_SIZE = 18;

export const ConnectWalletButton: FunctionComponent = () => {
  const allToken = useAppSelector((state) => state.meta.tokens);
  const { pathname } = useLocation();
  const { getWalletInfo } = useWallets();
  const { t } = useTranslation();
  const {
    isActiveTab,
    wallets: { details, totalBalance, isLoading },
  } = useWidget();
  const { push } = useStackModal();
  const shouldCheckIsActiveTab = pathname.includes(WIDGET_BASE_ROUTE);
  const disabled = shouldCheckIsActiveTab && !isActiveTab;
  const isBalanceLoading = isLoading;
  const sumWalletInUsd = totalBalance;
  const connectedWalletsTypes = Array.from(
    new Set(details.map((walletDetails) => walletDetails.walletType)),
  );

  const handleOpenWalletDetailsModal = () => {
    push({
      id: WALLET_DETAILS_MODAL_ID,
      renderContent: () => <WalletDetails />,
    });
  };

  const handleOpenConnectWalletsModal = () => {
    push({
      id: CONNECT_WALLETS_MODAL_ID,
      renderContent: (index) => (
        <ConnectWalletModal title={t('Connect Wallet')} indexInStack={index} />
      ),
    });
  };

  const handleClick = () => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: GTMEvents.CLICK_WALLET_CONNECT_TOP,
      });
    } catch {
      // do nothing here
    }
    if (connectedWalletsTypes?.length) {
      handleOpenWalletDetailsModal();
    } else {
      handleOpenConnectWalletsModal();
    }
  };

  const handleOpenConnectWalletsEvent = (event: UiEventData) => {
    if (event.type === UiEventTypes.CLICK_CONNECT_WALLET) {
      event.payload.preventDefault();
      handleOpenConnectWalletsModal();
    }
  };

  useEffect(() => {
    widgetEventEmitter.on(WidgetEvents.UiEvent, handleOpenConnectWalletsEvent);

    return () => {
      widgetEventEmitter.off(WidgetEvents.UiEvent, handleOpenConnectWalletsEvent);
    };
  }, [widgetEventEmitter]);

  return (
    <Flex direction={{ initial: 'column', md: 'row' }}>
      <Box display={{ initial: 'block', lg: 'none' }}>
        <IconButton
          id="mobile-header-connect-wallet-btn"
          aria-label={t('Connect wallet')}
          variant="ghost"
          color="cyan"
          radius="full"
          size="3"
          disabled={disabled || allToken.length === 0}
          className={styles.mobileTrigger}
          onClick={handleClick}>
          <Wallet size={WALLET_ICON_SIZE} />
          {!!connectedWalletsTypes?.length && <span className={styles.connectedBadge} />}
        </IconButton>
      </Box>
      {!connectedWalletsTypes?.length && (
        <Flex display={{ initial: 'none', lg: 'flex' }}>
          <Button
            id="header-connect-wallet-btn"
            size="3"
            radius="full"
            disabled={disabled || allToken.length === 0}
            onClick={handleClick}>
            <Wallet size={WALLET_ICON_SIZE} />
            {t('Connect wallet')}
          </Button>
        </Flex>
      )}
      {connectedWalletsTypes?.length > 0 && (
        <Flex align="center" flexShrink="0" display={{ initial: 'none', lg: 'flex' }}>
          <Button
            id="header-connected-wallets-btn"
            size="3"
            disabled={disabled}
            className={styles.connectedButton}
            onClick={handleOpenWalletDetailsModal}>
            <Tooltip
              side="bottom"
              container={getContainer()}
              open={connectedWalletsTypes.length > WALLET_DISPLAY_LIMIT ? undefined : false}
              content={connectedWalletsTypes.map((walletType, index) => (
                <img
                  key={walletType}
                  src={getWalletInfo(walletType).img}
                  alt={walletType}
                  className={clsx(styles.tooltipWallet, index > 0 && styles.tooltipWalletStacked)}
                />
              ))}>
              <span className={styles.wallets}>
                {connectedWalletsTypes.slice(0, WALLET_DISPLAY_LIMIT).map((walletType) => (
                  <img
                    key={walletType}
                    src={getWalletInfo(walletType).img}
                    alt={walletType}
                    className={styles.wallet}
                  />
                ))}
                {connectedWalletsTypes.length > WALLET_DISPLAY_LIMIT && (
                  <span className={clsx(styles.wallet, styles.moreWallets)}>
                    <Text size="1" weight="medium">
                      +{connectedWalletsTypes.length - WALLET_DISPLAY_LIMIT}
                    </Text>
                  </span>
                )}
              </span>
            </Tooltip>

            <Text size="3" className={styles.balance}>
              {isBalanceLoading && <Spinner className={styles.balanceSpinner} />}
              {!isBalanceLoading && !!sumWalletInUsd && `$${numberToString(sumWalletInUsd)}`}
            </Text>
          </Button>

          <Tooltip content={t('Add more wallets')} side="bottom" container={getContainer()}>
            <IconButton
              id="header-add-wallet-icon-btn"
              aria-label={t('Add more wallets')}
              size="3"
              disabled={disabled}
              className={styles.addWalletButton}
              onClick={handleOpenConnectWalletsModal}>
              <Plus size={ADD_ICON_SIZE} />
            </IconButton>
          </Tooltip>
        </Flex>
      )}
    </Flex>
  );
};
